import { getSupabaseClient } from "./supabase";
import { runtimeConfig } from "../config/runtimeConfig";

const PRIVATE_MEDIA_PREFIX = "supabase-private://";
const GATEWAY_PRIVATE_PREFIX = "gateway-private://story-assets/";
const SIGNED_URL_TTL_SECONDS = 60 * 60;
const MAX_ACCOUNT_MEDIA_BYTES = 20 * 1024 * 1024;
const signedMedia = new Map<string, { expiresAt: number; promise: Promise<string> }>();

export function directWebMediaUrl(value: string | null | undefined): string {
  const source = value?.trim() ?? "";
  if (/^https?:\/\//i.test(source) || /^blob:/i.test(source) || /^data:image\//i.test(source)) return source;
  return "";
}

export function isPrivateAccountMedia(value: string | null | undefined): boolean {
  return value?.startsWith(PRIVATE_MEDIA_PREFIX) ?? false;
}

export async function resolveAccountMediaUrl(value: string | null | undefined, motion = false): Promise<string> {
  const source = value?.trim() ?? "";
  const managed = source.match(/^mvp-media:\/\/([\da-f-]{36})(?:\/motion)?$/i);
  if (managed) {
    const supabase = getSupabaseClient(); if (!supabase) return '';
    const { data } = await supabase.auth.getSession(); if (!data.session) return '';
    const key = `${data.session.user.id}:${source}:${motion}`;
    const cached = signedMedia.get(key); if (cached && cached.expiresAt > Date.now()) return cached.promise;
    const promise = fetch(`${runtimeConfig.gatewayUrl.replace(/\/$/, '')}/v1/presentation-media/${managed[1]}${motion ? '' : '?still=1'}`, { headers: { authorization: `Bearer ${data.session.access_token}` } }).then(async response => response.ok ? URL.createObjectURL(await response.blob()) : '');
    if (!motion) signedMedia.set(key, { expiresAt: Date.now() + 3_300_000, promise }); return promise;
  }
  if (!motion && /\.(mp4|webm)(?:[?#]|$)|^data:video\//i.test(source)) return '';
  const direct = directWebMediaUrl(source);
  if (direct || !source) {
    if (direct && !motion && /\.gif(?:[?#]|$)|^data:image\/gif/i.test(source)) {
      try { const { webpFile } = await import('./mediaLibrary'); return URL.createObjectURL(await webpFile(await (await fetch(direct)).blob())); } catch { return ''; }
    }
    return direct;
  }
  if (!isPrivateAccountMedia(source) && !source.startsWith(GATEWAY_PRIVATE_PREFIX)) return "";

  const supabase=getSupabaseClient();if(!supabase)return '';
  const {data:auth}=await supabase.auth.getSession();if(!auth.session)return '';
  const cacheKey=`${auth.session.user.id}:${source}:${motion}`;
  const cached = signedMedia.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;
  const promise = (async () => {
    if (source.startsWith(GATEWAY_PRIVATE_PREFIX)) {
      const assetId = source.slice(GATEWAY_PRIVATE_PREFIX.length);
      if (!/^[0-9a-f-]{36}$/i.test(assetId)) return "";
      const response = await fetch(`${runtimeConfig.gatewayUrl.replace(/\/$/, "")}/v1/story-assets/${assetId}`, { headers: { authorization: `Bearer ${auth.session.access_token}` } });
      if (!response.ok) return "";
      return URL.createObjectURL(await response.blob());
    }
    const objectPath = source.slice(PRIVATE_MEDIA_PREFIX.length);
    if (!objectPath || objectPath.includes("..")) return "";
    const { data, error } = await supabase.storage.from("private-media").createSignedUrl(objectPath, SIGNED_URL_TTL_SECONDS);
    if (error) return "";
    return directWebMediaUrl(data.signedUrl);
  })();
  signedMedia.set(cacheKey, { expiresAt: Date.now() + (SIGNED_URL_TTL_SECONDS - 300) * 1000, promise });
  return promise;
}

function extensionForMedia(file: File): string {
  if (file.type === "image/png") return "png";
  if (file.type === "image/webp") return "webp";
  if (file.type === "image/gif") return "gif";
  return "jpg";
}

export async function uploadAccountPresentationMedia(file: File, userId: string): Promise<string> {
  if (!file.type.startsWith("image/")) throw new Error("เลือกได้เฉพาะไฟล์ภาพ");
  if (!file.size || file.size > MAX_ACCOUNT_MEDIA_BYTES) throw new Error("รูปต้องมีขนาดไม่เกิน 20 MB");
  const supabase = getSupabaseClient();
  if (!supabase || !userId) throw new Error("ต้องเข้าสู่ระบบก่อนอัปโหลดรูป");
  const digest = [...new Uint8Array(await crypto.subtle.digest("SHA-256", await file.arrayBuffer()))]
    .map((value) => value.toString(16).padStart(2, "0")).join("");
  const path = `${userId}/snapshot/${digest}.${extensionForMedia(file)}`;
  const { error } = await supabase.storage.from("private-media").upload(path, file, {
    upsert: true,
    contentType: file.type || "image/jpeg",
    cacheControl: "3600",
  });
  if (error) throw new Error(error.message || "อัปโหลดรูปไม่สำเร็จ");
  const reference = `${PRIVATE_MEDIA_PREFIX}${path}`;
  signedMedia.delete(reference);
  return reference;
}
